import useReveal from "./useReveal";

const Reveal = ({ children, delay = 0 }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

const HOBBIES = [
  "🏍 bikes",
  "🏋️ gym",
  "🍵 tea",
  "🍳 cooking",
  "📖 reading",
  "⚙️ tinkering",
];

const About = () => (
  <section id="about" className="section-pad" style={s.section}>
    <Reveal>
      <p style={s.label}>
        <span style={s.slash}>//</span> About
      </p>
    </Reveal>
    <div className="about-grid">
      <div>
        <Reveal delay={0.05}>
          <p style={s.p}>
            People often call me{" "}
            <code style={s.code}>
              oneOf(['Rama', 'Krishna', 'RK', 'Ramki', 'Kitty'])
            </code>
            . I'm a software engineer at{" "}
            <strong style={s.strong}>Bottomline Technologies</strong>, Bengaluru
            — building fintech infrastructure used by real businesses to move
            real money.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p style={s.p}>
            Three years in, I've stopped thinking of myself as someone who makes
            things look good on screens. I think of myself as someone who{" "}
            <strong style={s.strong}>reduces friction</strong> — between a
            business and its operations, between a user and their goal, between
            an idea and its execution.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p style={s.p}>
            I care deeply about{" "}
            <strong style={s.strong}>
              clean architecture, clarity, and maintainable code
            </strong>{" "}
            — not as checkboxes, but because I've seen what happens when you
            skip them. I want to keep growing across the stack because good
            engineering isn't a layer, it's a perspective.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p
            style={{
              ...s.p,
              fontFamily: "var(--serif)",
              fontStyle: "italic",
              color: "var(--muted)",
              fontSize: "0.95rem",
            }}
          >
            Constraints teach more than freedom ever could.
          </p>
        </Reveal>
      </div>

      <div>
        <Reveal delay={0.1}>
          <div style={s.card}>
            <div style={s.cardLabel}>// when I'm coding</div>
            <div style={s.cardBody}>
              Building clean, maintainable systems. Writing tests before someone
              asks. Leaving the codebase better than I found it. Probably
              questioning every naming decision for 10 minutes.
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div style={s.card}>
            <div style={s.cardLabel}>// when I'm not</div>
            <div style={s.cardBody}>
              <div style={s.chips}>
                {HOBBIES.map((h) => (
                  <span key={h} style={s.chip}>
                    {h}
                  </span>
                ))}
              </div>
              <br />
              Probably{" "}
              <code style={{ ...s.code, fontSize: "0.78rem" }}>
                console.log
              </code>
              -ing my thoughts on a weekend bike ride or over an evening tea,
              quietly observing the world around me.
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div style={s.card}>
            <div style={s.cardLabel}>// currently</div>
            <div style={s.cardBody}>
              <span style={s.dot} />
              Looking for engineering roles where the problems are real and the
              people think carefully.
              <br />
              <span style={{ color: "var(--muted)", fontSize: "0.8rem" }}>
                Bengaluru · Hyderabad · Remote
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const s = {
  section: { maxWidth: 1100, margin: "0 auto" },
  label: {
    fontSize: "0.68rem",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "var(--accent)",
    marginBottom: "2.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  slash: { color: "var(--muted)" },
  p: {
    fontSize: "0.9rem",
    color: "var(--muted2)",
    lineHeight: 1.95,
    marginBottom: "1.25rem",
  },
  strong: { color: "var(--text)", fontWeight: 500 },
  code: {
    fontFamily: "var(--mono)",
    fontSize: "0.8rem",
    background: "rgba(192,132,252,0.1)",
    border: "1px solid rgba(192,132,252,0.2)",
    color: "var(--accent2)",
    padding: "2px 7px",
    borderRadius: 3,
  },
  card: {
    background: "var(--bg2)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    padding: "1.5rem",
    marginBottom: "1rem",
  },
  cardLabel: {
    fontSize: "0.65rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "var(--accent)",
    marginBottom: "0.75rem",
  },
  cardBody: { fontSize: "0.84rem", color: "var(--muted2)", lineHeight: 1.8 },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.4rem",
    marginTop: "0.25rem",
  },
  chip: {
    fontSize: "0.72rem",
    border: "1px solid var(--border2)",
    color: "var(--muted2)",
    padding: "4px 10px",
    borderRadius: 20,
    letterSpacing: "0.04em",
  },
  dot: {
    display: "inline-block",
    width: 6,
    height: 6,
    background: "var(--green)",
    borderRadius: "50%",
    marginRight: 6,
    animation: "pulse-green 2s ease-in-out infinite",
  },
};

export default About;
